
import { Vue, Component } from 'vue-property-decorator';
import AnalisePreditivaTurnoverController from '@/assets/ts/controller/AnalisePreditivaTurnoverController';
import DateUtil from '@tekvue/assets/utils/DateUtil';
import DetalhesVinculoPopup from '@/components/AnalisePreditivaTurnover/DetalhesVinculoPopup.vue';
import { Mutation } from 'vuex-class';

@Component({
  name: 'AnalisePreditivaTurnover',
  components: {
    DetalhesVinculoPopup
  }
})
export default class AnalisePreditivaTurnover extends Vue {
  @Mutation('changeCurrentView')
  private changeCurrentView: (string) => void;
  
  private readonly viewTitle = 'Análise Preditiva de Turnover';
  private search = '';
  private filtroRisco: string | null = null;
  private showDetalhesPopup = false;
  private vinculoSelecionado: any = null;
  
  // #region [ LIFECYCLE ]
  private async created() {
    this.changeCurrentView(this.viewTitle);
  }
  // #endregion

  // Dados fictícios expandidos
  private vinculos = [
    {
      matricula: '001',
      nome: 'João Silva',
      cargo: 'Analista de RH',
      tempoCasa: '2 anos',
      risco: 'BAIXO',
      probabilidadeProcesso: '1%',
      detalhes: {
        "promocoes": 3,
        "reajustes": 2,
        "ferias": 1,
        "feriasAtrasadas": 0,
        "feriasAdiantadas": 0,
        "feriasParciais": 0,
        "feriasCompletas": 1,
        "feriasCompletasAtrasadas": 0,
        "faltas": 0,
        "advertencias": 0,
        "afastamentos": 0,
        "movimentacoesGerenciais": 0,
        "atrasosDePagamentoFolha": 0,
        "ambienteInsalubre": false,
        "ambientePericuloso": false,
        "mediaHorasExtraMes": 1.20
      }
    },
    {
      matricula: '002',
      nome: 'Maria Santos',
      cargo: 'Gerente de Vendas',
      tempoCasa: '5 anos',
      risco: 'ALTO',
      probabilidadeProcesso: '75%',
      detalhes: {
        "promocoes": 0,
        "reajustes": 1,
        "ferias": 5,
        "feriasAtrasadas": 0,
        "feriasAdiantadas": 0,
        "feriasParciais": 1,
        "feriasCompletas": 4,
        "feriasCompletasAtrasadas": 0,
        "faltas": 15,
        "advertencias": 2,
        "afastamentos": 3,
        "movimentacoesGerenciais": 4,
        "atrasosDePagamentoFolha": 0,
        "ambienteInsalubre": true,
        "ambientePericuloso": true,
        "mediaHorasExtraMes": 20.0
      }
    },
    {
      matricula: '003',
      nome: 'Pedro Oliveira',
      cargo: 'Desenvolvedor',
      tempoCasa: '1 ano',
      risco: 'MEDIO',
      probabilidadeProcesso: '45%',
      detalhes: {
        "promocoes": 0,
        "reajustes": 0,
        "ferias": 2,
        "feriasAtrasadas": 0,
        "feriasAdiantadas": 0,
        "feriasParciais": 0,
        "feriasCompletas": 2,
        "feriasCompletasAtrasadas": 0,
        "faltas": 5,
        "advertencias": 1,
        "afastamentos": 0,
        "movimentacoesGerenciais": 1,
        "atrasosDePagamentoFolha": 0,
        "ambienteInsalubre": true,
        "ambientePericuloso": false,
        "mediaHorasExtraMes": 10.0
      }
    },
    {
      matricula: '004',
      nome: 'Pedro Josiane',
      cargo: 'Desenvolvedor',
      tempoCasa: '14 anos',
      risco: 'MEDIO',
      probabilidadeProcesso: '85%',
      detalhes: {
        "promocoes": 0,
        "reajustes": 0,
        "ferias": 2,
        "feriasAtrasadas": 0,
        "feriasAdiantadas": 0,
        "feriasParciais": 0,
        "feriasCompletas": 2,
        "feriasCompletasAtrasadas": 0,
        "faltas": 5,
        "advertencias": 1,
        "afastamentos": 0,
        "movimentacoesGerenciais": 1,
        "atrasosDePagamentoFolha": 0,
        "ambienteInsalubre": true,
        "ambientePericuloso": false,
        "mediaHorasExtraMes": 10.0
      }
    }
  ];

  // Getters para contagens
  get totalVinculos(): number {
    return this.vinculos.length;
  }

  get vinculosRiscoBaixo(): number {
    return this.vinculos.filter(v => v.risco === 'BAIXO').length;
  }

  get vinculosRiscoMedio(): number {
    return this.vinculos.filter(v => v.risco === 'MEDIO').length;
  }

  get vinculosRiscoAlto(): number {
    return this.vinculos.filter(v => v.risco === 'ALTO').length;
  }

  // Getter para vinculos filtrados
  get vinculosFiltrados(): any[] {
    if (!this.filtroRisco) return this.vinculos;
    return this.vinculos.filter(v => v.risco === this.filtroRisco);
  }

  // Métodos para filtrar
  private filtrarPorRisco(risco: string): void {
    this.filtroRisco = this.filtroRisco === risco ? null : risco;
  }

  private limparFiltroRisco(): void {
    this.filtroRisco = null;
  }

  private headers = [
    { text: 'Nr. Vínculo', value: 'matricula' },
    { text: 'Nome', value: 'nome' },
    { text: 'Cargo', value: 'cargo' },
    { text: 'Tempo de Casa', value: 'tempoCasa' },
    { text: 'Risco de Turnover', value: 'risco' },
    { text: 'Prob. Processo Trabalhista', value: 'probabilidadeProcesso' },
    { text: 'Ações', value: 'acoes', sortable: false },
  ];

  private getRiscoColor(risco: string): string {
    const cores = {
      BAIXO: '#FDD835', // Amarelo
      MEDIO: '#FF9800', // Laranja
      ALTO: '#F44336', // Vermelho
    };
    return cores[risco] || 'grey';
  }

  private getRiscoIcon(risco: string): string {
    const icons = {
      BAIXO: 'mdi-alert-circle-outline', // Mudando o ícone
      MEDIO: 'mdi-alert',
      ALTO: 'mdi-alert-circle',
    };
    return icons[risco] || 'mdi-help';
  }

  private verDetalhes(item: any): void {
    this.vinculoSelecionado = item;
    this.showDetalhesPopup = true;
  }

  private customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
    return items.sort((a, b) => {
      for (let i = 0; i < sortBy.length; i++) {
        const column = sortBy[i];
        const desc = sortDesc[i];
        
        if (column === 'risco') {
          const riscoOrdem = { 'BAIXO': 1, 'MEDIO': 2, 'ALTO': 3 };
          const valorA = riscoOrdem[a.risco] || 0;
          const valorB = riscoOrdem[b.risco] || 0;
          
          if (valorA !== valorB) {
            return desc ? valorB - valorA : valorA - valorB;
          }
        } 
        else if (column === 'tempoCasa') {
          // vai extrair somente o numero (ex: "2 anos" -> 2)
          const getNumeroAnos = (tempo: string) => {
            const match = tempo.match(/^(\d+)/);
            return match ? parseInt(match[1], 10) : 0;
          };
          
          const anosA = getNumeroAnos(a.tempoCasa);
          const anosB = getNumeroAnos(b.tempoCasa);
          
          if (anosA !== anosB) {
            return desc ? anosB - anosA : anosA - anosB;
          }
        }
        else if (a[column] !== b[column]) {
          //ordena de forma padrão as outras colunas.
          return (a[column] < b[column]) !== desc ? -1 : 1;
        }
      }
      return 0;
    });
  }
}
